// dependencies.
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
// components.
import IconUsers from '../components/Icon/IconUsers'
import Head from '../components/Meta/Head'
import { Body } from '../components/Typography'
import { Input } from '../components/Form'
import { ScreenWrapper, Topbar, Wrapper } from '../components/Layout'
// utils.
import setBackgroundColor from '../js/setBackgroundColor'
import { useAppSelector } from '../js/store/hooks'
import { auth } from '../js/firebase/firebase'
import { selectUserData } from '../js/auth/authSelectors'
import { theme } from '../styleguide'

/******************************/
/*                            */
/*    Auth Screen Template    */
/*                            */
/******************************/

// partials.
const StyledIcon = styled(IconUsers)({
  margin: '24px auto',
})

const StyledBody = styled(Body)({
  margin: 'auto',
  maxWidth: '360px',
  padding: '16px',
  textAlign: 'center',
})

const FormWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  margin: '0 auto',
  maxWidth: '360px',
  width: '100%',
})

const FieldWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const FieldError = styled(Body)({
  color: theme.colors.alert,
  fontSize: 12,
  lineHeight: 1.3,
  margin: '0 16px',
})

// main component.
const AuthTemplate = ({
  disbledSubmit,
  description,
  form,
  submitLabel,
  topbarIcon,
  topbarTitle,
  onChange,
  onSubmit,
  onTopbarAction,
  formErrors = {},
  children,
}) => {
  // contexts.
  const userData = useAppSelector(selectUserData)
  // hooks.
  const navigate = useNavigate()
  // states.
  const [values, setValues] = useState({})
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  // ------------------------------------------------------------------------------------------- //
  // Handle actions                                                                              //
  // ------------------------------------------------------------------------------------------- //

  // handle update value change.
  const handleValueChange = (value, index) => {
    setValues({ ...values, [index]: value })
  }

  // ------------------------------------------------------------------------------------------- //
  // USER SESSION                                                                                //
  // ------------------------------------------------------------------------------------------- //

  useEffect(() => {
    // Wait for access in Redux before following redirects; otherwise /redirect-card can hang
    // with Firebase auth present but no role yet ("Redirigiendo...").
    if (!auth.currentUser || !userData?.access) return

    const redirectParam = queryParams.get('redirect') || ''
    const redirect = redirectParam ? decodeURIComponent(redirectParam) : ''

    if (redirect) {
      if (redirect.startsWith('/')) {
        navigate(redirect)
        return
      }
      const redirectPath = redirect.startsWith('?') ? redirect : `/${redirect}`
      if (userData.access === 'superadmin' || userData.access === 'admin' || userData.access === 'developer') {
        navigate(`/admin${redirectPath}`)
        return
      }
      if (userData.access === 'operator') {
        navigate(`/funcionario${redirectPath}`)
      }
      return
    }

    navigate(`/`)
  }, [auth.currentUser, userData.access, location.search])

  // ------------------------------------------------------------------------------------------- //
  // CONTENT                                                                                     //
  // ------------------------------------------------------------------------------------------- //

  // propagate value.
  useEffect(() => {
    if (!onChange) return
    onChange(values)
  }, [values])

  // return content.
  return (
    <>
      <Head color={setBackgroundColor(userData.isAuditor ? 'auditor' : userData.access)} />

      <ScreenWrapper
        background={setBackgroundColor(userData.isAuditor ? 'auditor' : userData.access)}
      >
        <Topbar iconL={topbarIcon} label={topbarTitle} onActionLPress={onTopbarAction} />

        <Wrapper background="white" actionLabel={submitLabel} onClick={!disbledSubmit && onSubmit}>
          <StyledIcon size={80} color="primary" />
          <StyledBody align="center" color="grey" setContent>
            {description}
          </StyledBody>

          <FormWrapper>
            {form.map((input, i) => (
              <FieldWrapper key={i}>
                <Input
                  iconL={input.iconL || false}
                  iconR={input.iconR || false}
                  iconRColor={input.iconRColor || 'ash'}
                  onIconRClick={input.handleIconRClick}
                  prefix={input.prefix || false}
                  name={input.name || i}
                  placeholder={input.placeholder}
                  disabled={input.disabled}
                  type={input.type || 'text'}
                  inputMode={input.inputMode}
                  autoComplete={input.autoComplete}
                  defaultValue={input.defaultValue || ''}
                  onChange={(value) => handleValueChange(value, input.name || i)}
                />
                {formErrors[input.name || i] ? <FieldError>{formErrors[input.name || i]}</FieldError> : null}
              </FieldWrapper>
            ))}
          </FormWrapper>

          {children}
        </Wrapper>
      </ScreenWrapper>

      <ToastContainer />
    </>
  )
}

export default AuthTemplate
